var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import classNames from 'classnames';
import styles from './SelectSearch.module.css';
import baseStyles from '../Base.module.css';
import { isMobileDevice, filterInputValue } from '../utils';
import { InputBase } from '../InputBase';
import { useEffect, useRef, useState } from 'react';
function useAvailableOptions(query, options, selected) {
    var _a = __read(React.useState([]), 2), availableOptions = _a[0], setAvailableOptions = _a[1];
    var _b = __read(React.useState(false), 2), isLoadingOptions = _b[0], setIsLoadingOptions = _b[1];
    var timeout = React.useRef();
    var firstRender = React.useRef(true);
    React.useEffect(function () {
        if (typeof options === 'function') {
            window.clearTimeout(timeout.current);
            if (query || firstRender.current) {
                firstRender.current = false;
                timeout.current = window.setTimeout(function () {
                    setIsLoadingOptions(true);
                    options(query)
                        .then(setAvailableOptions)
                        .then(function () { return setIsLoadingOptions(false); });
                }, 500);
            }
        }
    }, [options, query]);
    React.useEffect(function () {
        var isSelected = function (option) {
            if (selected) {
                return selected.name === option.name;
            }
            else {
                return false;
            }
        };
        var queryRegexp = new RegExp(query, 'i');
        var shouldBeVisible = function (option) {
            return !isSelected(option) && queryRegexp.test(option.name);
        };
        if (Array.isArray(options)) {
            setAvailableOptions(options.filter(shouldBeVisible));
        }
    }, [options, selected, query]);
    return { availableOptions: availableOptions, isLoadingOptions: isLoadingOptions };
}
function Dropdown(props) {
    var options = props.options, handleClick = props.handleClick, isLoadingOptions = props.isLoadingOptions;
    var dropdownRef = useRef(null);
    var _a = __read(useState({}), 2), dropdownStyle = _a[0], setDropdownStyle = _a[1];
    useEffect(function () {
        if (dropdownRef.current) {
            var top_1 = dropdownRef.current.getBoundingClientRect().top;
            var windowHeight = window.innerHeight;
            var maxHeight = windowHeight - top_1 - 18;
            var maxHeightInPx = "".concat(maxHeight, "px");
            setDropdownStyle({ maxHeight: maxHeightInPx });
        }
    }, []);
    return (React.createElement("div", { style: dropdownStyle, className: styles.dropdown, tabIndex: -1, ref: dropdownRef }, isLoadingOptions ? (React.createElement("div", { className: styles.listInfoItem }, "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430...")) : options.length ? (options.map(function (option, index) { return (React.createElement("div", { key: index, className: styles.listItem, onClick: function (event) {
            event.stopPropagation();
            handleClick(option);
        } }, option.name)); })) : (React.createElement("div", { className: styles.listInfoItem }, "\u041D\u0435\u0442 \u044D\u043B\u0435\u043C\u0435\u043D\u0442\u043E\u0432"))));
}
export function SelectSearch(props) {
    var _a = __read(React.useState(false), 2), active = _a[0], setActive = _a[1];
    var _b = __read(React.useState(''), 2), query = _b[0], setQuery = _b[1];
    var containerRef = React.useRef(null);
    var inputRef = React.useRef(null);
    var initialRender = React.useRef(true);
    var baseProps = __rest(props, []);
    React.useEffect(function () {
        if (initialRender.current) {
            initialRender.current = false;
        }
        else {
            if (!active) {
                inputRef.current.blur();
                setQuery('');
            }
        }
    }, [active]);
    var _c = useAvailableOptions(query, props.options, props.selected), availableOptions = _c.availableOptions, isLoadingOptions = _c.isLoadingOptions;
    var handleContainerClick = function () {
        if (props.disabled)
            return;
        setActive(true);
        // focus on ios safari: https://stackoverflow.com/a/16601288
        inputRef.current.focus();
        if (isMobileDevice()) {
            // to avoid jitter
            setTimeout(function () {
                containerRef.current.scrollIntoView({ block: 'start' });
            }, 100);
        }
    };
    var handleChange = function (event) {
        filterInputValue(event, setQuery);
    };
    var handleBlur = function (event) {
        var relatedTarget = event.relatedTarget;
        var container = containerRef.current;
        if (relatedTarget && relatedTarget.parentElement === container) {
            inputRef.current.focus();
        }
        else {
            setActive(false);
        }
    };
    var handleKeyDown = function (event) {
        if (event.key === 'Escape') {
            setActive(false);
        }
    };
    var handleItemSelect = function (option) {
        props.updateSelected(option);
        setActive(false);
    };
    return (React.createElement(InputBase, __assign({}, baseProps, { inputComponent: function (restProps) {
            var _a, _b;
            var _c, _d;
            if (!restProps.value)
                restProps.className = classNames(restProps.className, styles.placeholder);
            return (React.createElement("div", { ref: containerRef, tabIndex: -1, className: classNames(baseStyles.controlInput, styles.singleSearch, (_a = {},
                    _a[baseStyles.controlInputInvalid] = props.isValid === false,
                    _a[styles.disabled] = props.disabled,
                    _a)), onClick: handleContainerClick },
                React.createElement("input", { ref: inputRef, value: query, placeholder: (_d = (_c = props.selected) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : props.placeholder, id: props.id, className: classNames(baseStyles.controlInput, styles.queryInput, (_b = {},
                        _b[styles['queryInput--selected']] = props.selected,
                        _b)), onChange: handleChange, onBlur: handleBlur, onKeyDown: handleKeyDown, disabled: props.disabled }),
                active && (React.createElement(Dropdown, { options: availableOptions, isLoadingOptions: isLoadingOptions, handleClick: handleItemSelect }))));
        } })));
}
